import { FC, useEffect, useMemo, useState } from "react";
//#region types
import { TodoCardProps } from "models/propTypes/todoPropTypes";
import { CurrencyEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelWintSalaryReport } from "wint-api-client/dist/types";
//#endregion
//#region components
import { EntityCardContent, TitledContainerInner } from "components/common";
import TopTodoCard from "components/todos/_partials/topTodoCard";
import { WintSalaryReportTable } from "components/wintSalary";
import { useWintSalaryService } from "serviceHooks";
import { outstandingRegularAmount } from "components/wintSalary/_partials/payoutLines";
import SalaryCostBreakDownSummary from "./salaryCostBreakDownSummary";
import WintDeviationTodoFooter from "./todoFooter";
import WintSalaryApprovalAlerts from "./wintSalaryApprovalAlerts";
import { approvalPayoutView } from "./payoutWording";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import { DefaultPayoutDay } from "utils/constants/salaryDeviationConstants";

const WintSalaryAprrovalTodo: FC<TodoCardProps> = (props) => {
  const wintSalaryReport = useWintSalaryService();

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<WintSuperkollAPIModelWintSalaryReport.SalaryCostSummary>();
  const [dataRows, setDataRows] = useState<WintSuperkollAPIModelWintSalaryReport.WintSalaryReportRow[]>();
  const [selectedRows, setSelectedRows] = useState<WintSuperkollAPIModelWintSalaryReport.WintSalaryReportRow[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await wintSalaryReport.getDueMonthSalaryApprovalReport();
      const dataReport = response.data;
      const updatedRows = dataReport.personSummaries
        ?.sort((a, b) => {
          const aApproved = a.columns?.[0]?.isAlreadyApproved ? 1 : -1;
          const bApproved = b.columns?.[0]?.isAlreadyApproved ? 1 : -1;
          return aApproved - bApproved;
        })
        .map((row) => (isRowApproved(row) ? { ...row, selected: true, disabled: true } : { ...row, selected: true }));
      setDataRows(updatedRows ?? []);
      setSummary(dataReport.salaryCostSummary);
      setSelectedRows((updatedRows ?? []).filter((row) => !isRowApproved(row)));
    } catch (error) {
      // Handle error if needed
    } finally {
      setLoading(false);
    }
  };

  const getHiddenColumns = (
    rows: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportRow[]
  ): (keyof WintSuperkollAPIModelWintSalaryReport.WintSalaryReportRowColumn)[] => {
    const columns: (keyof WintSuperkollAPIModelWintSalaryReport.WintSalaryReportRowColumn)[] = [
      "startDate",
      "salaryYearMonth",
      "workedHours",
      "absenceDurationInDays",
      "timeBalancesHours",
      "compensationsValue",
      "salaryEventCategory",
      "amount",
      "state",
    ];

    const getAllColumns = (
      rowList: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportRow[]
    ): WintSuperkollAPIModelWintSalaryReport.WintSalaryReportRowColumn[] => {
      const columns: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportRowColumn[] = [];

      for (const row of rowList) {
        if (row.columns && row.columns.length > 0) {
          columns.push(row.columns[0]);
        }

        if (row.children && row.children.length > 0) {
          columns.push(...getAllColumns(row.children));
        }
      }

      return columns;
    };

    const allColumns = getAllColumns(rows);

    if (allColumns.length === 0) {
      return [];
    }

    const hiddenColumns: (keyof WintSuperkollAPIModelWintSalaryReport.WintSalaryReportRowColumn)[] = [];

    for (const property of columns) {
      const isHidden = allColumns.every((column) => column[property] == null);
      if (isHidden) {
        hiddenColumns.push(property);
      }
    }
    hiddenColumns.push("state");
    return hiddenColumns;
  };

  const isRowApproved = (baseRow: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportRow) => {
    return baseRow.columns?.[0]?.isAlreadyApproved;
  };

  const lastMonth = useMemo(() => DateHelper.getYearMonth(DateHelper.addMonths(DateHelper.today(), -1)), []);

  const month = useMemo(
    () =>
      DateHelper.getDateFromYearMonth(props.item.wintSalaryReport?.periodStartYearAndMonth ?? lastMonth).getMonth() + 1,
    [props.item.wintSalaryReport?.periodStartYearAndMonth, lastMonth]
  );

  const defaultPayoutDate = useMemo(() => DateHelper.getDayOfMonth(DateHelper.today(), DefaultPayoutDay), []);

  const regularPayoutDate = useMemo(
    () => (summary?.paymentDate ? new Date(summary.paymentDate) : defaultPayoutDate),
    [summary?.paymentDate, defaultPayoutDate]
  );

  // The actual payout date if approved right now — bank-cutoff (16:50) and holiday aware, computed
  // by the backend. Equals the regular payday until the approval deadline has actually passed.
  const effectivePayoutDate = useMemo(
    () =>
      summary?.actualPayoutDateIfApprovedToday
        ? new Date(summary.actualPayoutDateIfApprovedToday)
        : regularPayoutDate,
    [summary?.actualPayoutDateIfApprovedToday, regularPayoutDate]
  );

  const netSplit = useMemo(() => {
    if (!dataRows) {
      return { toApproveNow: 0, alreadyApproved: 0 };
    }
    // These two answer different questions and deliberately do not partition the month's net:
    // toApproveNow is what THIS approval releases, alreadyApproved is the off-payday extras.
    // Regular-payday money that is already a payout request falls outside both — as it did before
    // DEV-28550 for any already-approved row, since its regular amount was never counted either.
    // Nothing renders the two as a sum.
    let alreadyApproved = 0;
    let toApproveNow = 0;
    for (const row of dataRows) {
      const offPayday = row.payoutDetails?.earlyPayoutsAmount ?? 0;
      // Off-payday payouts (förskott / extra on chosen dates) are already approved, any state except won't-be-paid.
      alreadyApproved += offPayday;
      // The regular-payday amount of rows still to approve is what this approval releases.
      if (!isRowApproved(row)) {
        toApproveNow += outstandingRegularAmount(row.payoutDetails?.payouts, row.payoutDetails?.regularPayoutAmount ?? 0);
      }
    }
    return { toApproveNow, alreadyApproved };
  }, [dataRows]);

  const hasAlreadyApprovedExtras = netSplit.alreadyApproved > 0;

  const view = approvalPayoutView({
    customerPaysOut: summary?.customerPaysOut ?? false,
    regularPayoutDate,
    effectivePayoutDate,
    latestApprovalDate: summary?.latestApprovalDate
      ? new Date(summary.latestApprovalDate)
      : DateHelper.addDays(defaultPayoutDate, -2),
    today: new Date(),
  });
  const payoutWhen = view.payAsSoonAsPossible
    ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.WintTodoCustomerPayAsSoonAsPossible)
    : LocalizationHelperInstance.getFormattedDate(view.payoutDate);

  const onRowsUpdate = (rows: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportRow[]) => {
    const updatedRows = rows.filter((row) => !isRowApproved(row));
    setSelectedRows(updatedRows);
  };

  const reloadTodo = (onMarkAsDone?: () => void) => {
    fetchData();
    if (selectedRows?.length === (dataRows ?? []).filter((row) => !isRowApproved(row)).length) {
      props.onActionSuccess?.();
      onMarkAsDone?.();
    }
  };

  const topCardNetAmount = hasAlreadyApprovedExtras
    ? netSplit.toApproveNow
    : (summary?.remainingNetAmountToBePaid ?? 0);

  const topCardLabel = hasAlreadyApprovedExtras
    ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.WintTodoAmountToApprove)
    : summary?.anyPriorPayments
      ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.WintTodoRemainingNetSalary)
      : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.NetSalary);

  const monthText = `${topCardLabel} ${LocalizationHelperInstance.getFormattedMessage(`Common.Month_${month}`)}`;
  const payoutDateText = `${LocalizationHelperInstance.getFormattedMessage(view.payoutDateLabelCapitalized)}: ${payoutWhen}`;
  const approvalDateText = view.isLate
    ? LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.SalaryDeviation.WintTodoApproveNowForPayoutOn,
        "",
        { date: LocalizationHelperInstance.getFormattedDate(view.payoutDate) }
      )
    : view.approveBy
      ? `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.WintTodoApproveBefore)}: ${LocalizationHelperInstance.getFormattedDate(view.approveBy)}`
      : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.WintTodoApproveAsSoonAsPossible);

  return (
    <EntityCardContent
      loading={loading}
      topTodoCard={
        <TopTodoCard
          item={props.item}
          descriptionHeadingOne={LocalizationHelperInstance.getFormattedCurrency(
            topCardNetAmount,
            CurrencyEnums.CurrencyCode.SEK
          )}
          descriptionHeadingTwo={monthText}
          descriptionheadingThree={payoutDateText}
          footerText={approvalDateText}
        />
      }
      todoMainSection={
        <TitledContainerInner
          header={LocalizationHelperInstance.getFormattedMessage(view.header)}
          subHeader={LocalizationHelperInstance.getFormattedMessage(view.description)}
        >
          <SalaryCostBreakDownSummary
            totalSalaryCost={summary?.totalSalaryCost ?? 0}
            monthlyDeviationPercentage={summary?.monthlyChangePercentage ?? 0}
            netSalaryCost={summary?.remainingNetAmountToBePaid ?? 0}
            socialFeesCost={summary?.totalSocialFee ?? 0}
            personalTaxesCost={summary?.totalPersonalTax ?? 0}
            payoutDate={view.payoutDate}
            payoutWhen={payoutWhen}
            loading={loading}
            netToApproveNow={netSplit.toApproveNow}
            netAlreadyApproved={netSplit.alreadyApproved}
            payoutDateLabel={view.payoutDateLabel}
          />
        </TitledContainerInner>
      }
      infoViewer={
        <WintSalaryReportTable
          rows={dataRows}
          allowCheckBoxSelection
          onRowsChange={onRowsUpdate}
          reloadHandler={reloadTodo}
          hiddenColumns={getHiddenColumns(dataRows ?? [])}
          loading={loading}
          allowGrouping
          expandFirstRowByDefault={dataRows?.length === 1}
          regularPayoutDate={view.payoutDate}
          unscheduledPayoutLabel={
            view.outstandingRowLabel &&
            LocalizationHelperInstance.getFormattedMessage(view.outstandingRowLabel, undefined, {
              date: LocalizationHelperInstance.getFormattedDate(view.payoutDate),
            })
          }
        />
      }
      footer={
        <WintDeviationTodoFooter
          selectedRows={selectedRows}
          yearMonth={props.item.wintSalaryReport?.periodStartYearAndMonth ?? lastMonth}
          reloadHandler={reloadTodo}
        />
      }
    >
      <WintSalaryApprovalAlerts hasAlreadyApprovedExtras={hasAlreadyApprovedExtras} />
    </EntityCardContent>
  );
};

export default WintSalaryAprrovalTodo;
